/** Help (Support) — LOTE M. URL: /help */
import { useEffect, useState } from 'react';
import { useWww } from './_layout';
import { wwwTheme, wwwRadius, wwwFonts } from '@/src/website/theme';
import { SeoHead } from '@/src/website/SeoHead';

// Web equivalent of RN's useWindowDimensions().
function useWindowDimensions() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

export default function Help() {
  const { t, lang } = useWww();
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  // Google Play requires a public URL for account deletion (Data safety form).
  // `/delete-account` redirects here with `#delete-account` in the hash, so we
  // auto-expand the corresponding FAQ (Q7) and scroll it into view. Q7 also
  // carries `id="delete-account"` so direct anchor links work verbatim.
  const initialOpen = typeof window !== 'undefined' && window.location.hash === '#delete-account' ? 6 : 0;
  const [open, setOpen] = useState<number | null>(initialOpen);
  const faqs = [
    { q: t.helpQ1, a: t.helpA1 }, { q: t.helpQ2, a: t.helpA2 }, { q: t.helpQ3, a: t.helpA3 },
    { q: t.helpQ4, a: t.helpA4 }, { q: t.helpQ5, a: t.helpA5 }, { q: t.helpQ6, a: t.helpA6 },
    { q: t.helpQ7, a: t.helpA7 }, { q: t.helpQ8, a: t.helpA8 }, { q: t.helpQ9, a: t.helpA9 },
  ];
  // FAQPage Schema.org — enables rich snippets in Google search results.
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  // Web replacement for <Stack.Screen options={{ title: ... }} />: update
  // document.title so the browser tab matches the string the native screen
  // header used in the Expo version. Runs after SeoHead's effect so it has
  // the final word, exactly as in the original render order.
  useEffect(() => {
    document.title = t.helpTitle + ' · STILGIA';
  }, [t.helpTitle]);

  // Smooth-scroll to the delete-account FAQ when arriving with `#delete-account`
  // in the URL (both directly and via the `/delete-account` redirect route).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#delete-account') return;
    const el = document.getElementById('delete-account');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [lang]);

  return (
    <>
      <SeoHead page="help" locale={lang} jsonLd={faqJsonLd} />

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingTop: 64, paddingBottom: 32, maxWidth: 820, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <h1 style={{ color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: isMobile ? 34 : 46, marginTop: 0, marginBottom: 0 }}>{t.helpTitle}</h1>
        <p style={{ color: wwwTheme.muted, fontSize: 16, marginTop: 12, marginBottom: 0 }}>{t.helpSubtitle}</p>
      </div>

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingBottom: 32, maxWidth: 820, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <h2 style={{ color: wwwTheme.text, fontSize: 22, fontWeight: 600, marginTop: 0, marginBottom: 16, fontFamily: wwwFonts.serifBold }}>{t.helpFaqTitle}</h2>
        {faqs.map((f, i) => (
          <div key={i} id={i === 6 ? 'delete-account' : undefined} style={{ borderBottomColor: wwwTheme.border, borderBottomWidth: 1, borderBottomStyle: 'solid', scrollMarginTop: 24 }}>
            <button onClick={() => setOpen(open === i ? null : i)} style={{ paddingTop: 18, paddingBottom: 18, paddingLeft: 0, paddingRight: 0, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }}>
              <span style={{ color: wwwTheme.text, fontSize: 16, fontWeight: 500, flex: 1 }}>{f.q}</span>
              <span style={{ color: wwwTheme.muted, fontSize: 18, marginLeft: 12 }}>{open === i ? '–' : '+'}</span>
            </button>
            {open === i && (
              <p style={{ color: wwwTheme.muted, fontSize: 14, lineHeight: '22px', paddingBottom: 18, marginTop: 0, marginBottom: 0, whiteSpace: 'pre-line' }}>{f.a}</p>
            )}
          </div>
        ))}
      </div>

      <div style={{ paddingLeft: isMobile ? 24 : 48, paddingRight: isMobile ? 24 : 48, paddingBottom: 80, maxWidth: 820, width: '100%', marginLeft: 'auto', marginRight: 'auto', boxSizing: 'border-box' }}>
        <div style={{ backgroundColor: wwwTheme.surface, borderColor: wwwTheme.border, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.xl, padding: 28 }}>
          <h2 style={{ color: wwwTheme.text, fontSize: 20, fontWeight: 600, fontFamily: wwwFonts.serifBold, marginTop: 0, marginBottom: 0 }}>{t.helpContactTitle}</h2>
          <p style={{ color: wwwTheme.muted, fontSize: 14, lineHeight: '21px', marginTop: 8, marginBottom: 16 }}>{t.helpContactBody}</p>
          <button data-testid="help-support-email-button" onClick={() => { window.location.href = 'mailto:support@stilgia.com'; }} style={{ alignSelf: 'flex-start', backgroundColor: wwwTheme.text, borderRadius: wwwRadius.pill, paddingLeft: 22, paddingRight: 22, paddingTop: 12, paddingBottom: 12, border: 'none', cursor: 'pointer', font: 'inherit' }}>
            <span style={{ color: wwwTheme.onPrimary, fontWeight: 600, fontSize: 13 }}>support@stilgia.com</span>
          </button>
        </div>
      </div>
    </>
  );
}
